import { useLazyQuery } from "@apollo/client";
import { getHashParams } from "@neonumy/ant-design";
import { API } from "@neonumy/api-clients";
import i18n from "config/i18n/i18n";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { CHECK_USER_AUTHENTICATED } from "queries/neoAuth";
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { onErrorGraphql } from "utils/onErrorGraphql";
import Base from "views/Base";
import useUserDataStore from "../../hooks/useUserDataStore";
const ACCESS_PERMISSION = "neotreso.can-access";

export default function PrivateRoute(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const relativePathName = `${location.pathname}${location.search}`;
  const relativeUrlPath = useRef("");

  const { userData, setUserData } = useUserDataStore();

  const [getUserData, { data: getUserDataResult, error: getUserDataError }] =
    useLazyQuery<GetUserData>(CHECK_USER_AUTHENTICATED);

  useEffect(() => {
    if (getUserDataError) onErrorGraphql(getUserDataError);
  }, [getUserDataError]);

  useEffect(() => {
    if (!getUserDataResult) return;
    const { getUserByToken } = getUserDataResult;
    if (getUserByToken) {
      const canAccess = getUserByToken?.permissions
        ? getUserByToken.permissions.findIndex(permission => permission === ACCESS_PERMISSION) > -1
        : false;
      setUserData(getUserByToken);
      i18n.changeLanguage(getUserByToken.language.toLowerCase());
      localStorage.setItem("language", getUserByToken.language.toLowerCase());

      if (!canAccess) {
        navigate("/no-access");
      } else {
        navigate(relativeUrlPath.current, { replace: true });
      }
    } else {
      window.location.href = API.fullLoginUrl;
    }
  }, [getUserDataResult]);

  const handleLoginUseEffect = async () => {
    relativeUrlPath.current = relativePathName;

    if (location.hash) {
      const expireSeconds = getHashParams(location, "expires_in");
      const expireDate = dayjs().add(Number(expireSeconds) * 1000, "ms");
      const accessTokenValue = getHashParams(location, "access_token");

      Cookies.set("access_token", accessTokenValue, {
        path: "/",
        expires: expireDate.toDate(),
      });
      Cookies.set("expireDate", expireDate.toISOString(), {
        path: "/",
        expires: expireDate.toDate(),
      });
    }

    if (location.search) {
      const urlParams = new URLSearchParams(location.search);
      const relativeUrlPathParam = urlParams.get("relative_url_path");
      if (relativeUrlPathParam) {
        relativeUrlPath.current = relativeUrlPathParam;
      }
    }

    const cookieAccessToken = Cookies.get("access_token");
    if (cookieAccessToken && !userData) {
      await getUserData();
    } else {
      window.location.href = API.fullLoginUrl;
      return;
    }
  };

  useEffect(() => {
    handleLoginUseEffect();
  }, []);

  return <>{userData && <Base />}</>;
}
